import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import BoxGrid from "../../components/BoxGrid"
import CalloutBanner from "../../components/CalloutBanner"
import ButtonLink from "../../components/ButtonLink"
import Emoji from "../../components/Emoji"
import Translation from "../../components/Translation"
import { translateMessageId } from "../../utils/translations"
import { Page, Content, Divider } from "../../components/SharedStyledComponents"

const H2 = styled.h2`
  margin-top: 3rem;
  margin-bottom: 2rem;
`

const Text = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text200};
  max-width: 800px;
  margin-bottom: 2rem;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 4rem;
  @media (max-width: ${(props) => props.theme.breakpoints.s}) {
    flex-direction: column;
  }
`

const StyledCalloutBanner = styled(CalloutBanner)`
  margin: 4rem 0;
  .gatsby-image-wrapper {
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`

const ControlAccesoPage = ({ data }) => {
  const intl = useIntl()

  const heroContent = {
    title: translateMessageId("page-control-acceso-hero-title", intl),
    header: translateMessageId("page-control-acceso-hero-header", intl),
    subtitle: translateMessageId("page-control-acceso-hero-subtitle", intl),
    image: data.hero.childImageSharp.fluid,
    alt: "ControlAccess Dashboard",
    buttons: [
      {
        content: translateMessageId("page-control-acceso-btn-app", intl),
        path: "https://access-ai.newinntech.com/docs",
      },
      {
        content: translateMessageId("page-control-acceso-btn-docs", intl),
        path: "https://access-ai.newinntech.com/docs/api-reference/api-reference",
        isSecondary: "isSecondary",
      },
    ],
  }

  const features = [
    {
      title: translateMessageId("page-control-acceso-feature-title-1", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-1",
        intl
      ),
      emoji: ":eye:",
    },
    {
      title: translateMessageId("page-control-acceso-feature-title-2", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-2",
        intl
      ),
      emoji: ":ticket:",
    },
    {
      title: translateMessageId("page-control-acceso-feature-title-3", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-3",
        intl
      ),
      emoji: ":closed_lock_with_key:",
    },
    {
      title: translateMessageId("page-control-acceso-feature-title-4", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-4",
        intl
      ),
      emoji: ":bell:",
    },
    {
      title: translateMessageId("page-control-acceso-feature-title-5", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-5",
        intl
      ),
      emoji: ":chart_with_upwards_trend:",
    },
    {
      title: translateMessageId("page-control-acceso-feature-title-6", intl),
      description: translateMessageId(
        "page-control-acceso-feature-desc-6",
        intl
      ),
      emoji: ":link:",
    },
  ]

  return (
    <Page>
      <PageMetadata
        title={translateMessageId("page-control-acceso-meta-title", intl)}
        description={translateMessageId("page-control-acceso-meta-desc", intl)}
      />
      <PageHero content={heroContent} />

      <Content>
        <H2>
          <Translation id="page-control-acceso-section-title" />{" "}
          <Emoji text=":shield:" size={1} />
        </H2>
        <Text>
          <Translation id="page-control-acceso-section-desc" />
        </Text>

        <BoxGrid items={features} />

        <Divider />

        <StyledCalloutBanner
          title={translateMessageId("page-control-acceso-callout-title", intl)}
          description={translateMessageId(
            "page-control-acceso-callout-desc",
            intl
          )}
          image={data.callout.childImageSharp.fluid}
          alt="ControlAccess Dashboard Reports"
          maxImageWidth={450}
        >
          <ButtonRow>
            <ButtonLink to="https://access-ai.newinntech.com/docs/monitoreo-porteria/validacion-porteria">
              <Translation id="page-control-acceso-btn-app-secondary" />
            </ButtonLink>
            <ButtonLink
              isSecondary
              to="https://access-ai.newinntech.com/docs/panel-administrador/metricas-administrador"
            >
              <Translation id="page-control-acceso-btn-docs-secondary" />
            </ButtonLink>
          </ButtonRow>
        </StyledCalloutBanner>
      </Content>
    </Page>
  )
}

export default ControlAccesoPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "control_access_hero.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    callout: file(relativePath: { eq: "control_access_callout.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
